import type { MapPagePresenter } from '@Map/FloorMap/Components/MapPage/MapPagePresenter';
import { getMapPagePresenter } from '@Map/FloorMap/Components/MapPage/MapPagePresenter';
import { useMiniMap2Presenter } from '@Map/MiniMap2/MiniMap2Presenter';
import type { TransientClass } from '@Utils/useClassStore';
import { injectable } from 'inversify';
import { computed } from 'mobx';
import { createContext, useContext, useEffect, useState } from 'react';

import container from '~ioc/ioc';
import type { FloorStore } from '~stores/floor';

@injectable()
export class MiniMapLayoutPresenter implements TransientClass {
  private _floorStore?: FloorStore;

  @computed get title(): string | undefined {
    return this._floorStore?.data?.title;
  }

  init = (floorStore: FloorStore) => {
    this._floorStore = floorStore;
  };

  destroy = () => {
    /* do nothing */
  };
}

container.bind(MiniMapLayoutPresenter).toSelf().inTransientScope();

export const getMiniMapLayoutPresenter = (floorStore: FloorStore) => {
  const presenter = container.get(MiniMapLayoutPresenter);
  presenter.init(floorStore);

  return presenter;
};

export const MiniMapLayoutPresenterContext =
  createContext<MiniMapLayoutPresenter | null>(null);

export const useMiniMapLayoutPresenter = (floorId: number) => {
  const { activeFloorStore } = useMiniMap2Presenter();

  const [presenter, setPresenter] = useState<
    MiniMapLayoutPresenter | undefined
  >(undefined);

  const [mapPagePresenter] = useState<MapPagePresenter>(
    getMapPagePresenter(floorId)
  );

  useEffect(() => {
    if (activeFloorStore) {
      setPresenter(getMiniMapLayoutPresenter(activeFloorStore));
    }

    return () => {
      presenter?.destroy();
      mapPagePresenter?.destroy();
    };
  }, [activeFloorStore]);

  return { miniMapLayoutPresenter: presenter, mapPagePresenter };
};

// noinspection JSUnusedGlobalSymbols
export const useMiniMapLayoutPresenterContext = () => {
  const presenter = useContext(MiniMapLayoutPresenterContext);

  if (!presenter) {
    throw new Error(
      'FloorMap Error: Context value was null for MapPagePresenter'
    );
  }

  return presenter;
};
